<template>
  <div class="references-picker">
    <div class="d-flex justify-content-between align-items-center mb-50">
      <h6 class="mb-0">
        {{ $t('ia.contents.references') }}
        <small class="text-muted font-weight-normal">({{ $t('ia.contents.optional') }})</small>
      </h6>
      <small class="text-muted">{{ value.length }}/{{ max }}</small>
    </div>
    <p class="text-muted small mb-1">{{ $t('ia.contents.referencesHelp') }}</p>

    <div v-if="max === 0" class="text-muted small mb-1">{{ $t('ia.contents.referencesNotSupported') }}</div>
    <template v-else>
      <div class="references-row">
        <!-- Selected references -->
        <div v-for="reference in value" :key="reference.uuid" class="reference-thumb">
          <img :src="reference.url" :alt="reference.name" />
          <span class="reference-kind">{{ $t(`ia.contents.referenceKinds.${reference.kind}`) }}</span>
          <button type="button" class="reference-remove" :title="$t('ia.contents.removeReference')" @click="remove(reference)">
            <feather-icon icon="XIcon" size="12"></feather-icon>
          </button>
        </div>

        <!-- Uploading -->
        <div v-for="index in uploading_count" :key="`uploading_${index}`" class="reference-thumb reference-uploading">
          <b-spinner small></b-spinner>
        </div>

        <!-- Drop zone -->
        <div
          v-if="remaining > 0"
          class="reference-drop"
          :class="{ dragging }"
          @click="$refs.file_input.click()"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <feather-icon icon="UploadCloudIcon" size="20"></feather-icon>
          <span>{{ $t('ia.contents.uploadReference') }}</span>
          <small>JPG, PNG · 10 MB</small>
        </div>
      </div>

      <input
        ref="file_input"
        type="file"
        class="d-none"
        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
        multiple
        @change="onFilesSelected"
      />

      <div class="d-flex align-items-center flex-wrap references-actions">
        <div class="reference-kind-select">
          <v-select
            v-model="kind"
            :options="kind_options"
            label="text"
            :reduce="(option) => option.value"
            :clearable="false"
            :searchable="false"
          ></v-select>
        </div>
        <small class="text-muted mr-1">{{ $t('ia.contents.referenceKindHelp') }}</small>
        <b-button variant="outline-primary" size="sm" class="ml-auto" @click="show_library = true">
          <feather-icon icon="FolderIcon" class="mr-25"></feather-icon>{{ $t('ia.contents.fromLibrary') }}
        </b-button>
      </div>
    </template>

    <div v-for="(error, index) in all_errors" :key="index" class="text-danger small mt-50">{{ error }}</div>

    <references-library
      :visible="show_library"
      :selected="value"
      :max="max"
      @apply="applyLibrary"
      @close="show_library = false"
    ></references-library>
  </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import vSelect from 'vue-select';
import { uploadContentReferences } from '@/services/ai-contents';
import { REFERENCE_KINDS, validateReferenceFile, parseRequestErrors } from './contents-utils';

export default {
  name: 'ReferencesPicker',
  components: {
    BButton,
    BSpinner,
    vSelect,
    ReferencesLibrary: () => import('./ReferencesLibrary.vue'),
  },
  props: {
    // Selected reference objects
    value: {
      type: Array,
      default: () => [],
    },
    max: {
      type: Number,
      default: 4,
    },
    errors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      kind: 'product',
      dragging: false,
      uploading_count: 0,
      upload_errors: [],
      show_library: false,
    };
  },
  computed: {
    remaining() {
      return Math.max(this.max - this.value.length - this.uploading_count, 0);
    },
    kind_options() {
      return REFERENCE_KINDS.map((kind) => ({ value: kind, text: this.$t(`ia.contents.referenceKinds.${kind}`) }));
    },
    all_errors() {
      return [...this.upload_errors, ...this.errors];
    },
  },
  methods: {
    onDrop(event) {
      this.dragging = false;
      this.uploadFiles(Array.from(event.dataTransfer.files || []));
    },
    onFilesSelected(event) {
      this.uploadFiles(Array.from(event.target.files || []));
      event.target.value = '';
    },
    async uploadFiles(files) {
      this.upload_errors = [];
      if (!files.length) return;

      const valid = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const error = await validateReferenceFile(file);
        if (error) this.upload_errors.push(`${file.name}: ${this.$t(error)}`);
        else valid.push(file);
      }

      if (valid.length > this.remaining) {
        this.upload_errors.push(this.$t('ia.contents.referencesLimit', { max: this.max }));
      }
      const to_upload = valid.slice(0, this.remaining);
      if (!to_upload.length) return;

      this.uploading_count += to_upload.length;
      uploadContentReferences(to_upload, this.kind).then((response) => {
        const uploaded = Array.isArray(response) ? response : (response.results || []);
        this.$emit('input', [...this.value, ...uploaded].slice(0, this.max));
      }).catch((error) => {
        const parsed = parseRequestErrors(error);
        const messages = [...parsed.general, ...parsed.references];
        this.upload_errors.push(...(messages.length ? messages : [this.$t('ia.contents.errorUploadReference')]));
      }).finally(() => {
        this.uploading_count -= to_upload.length;
      });
    },
    remove(reference) {
      this.$emit('input', this.value.filter((item) => item.uuid !== reference.uuid));
    },
    applyLibrary(references) {
      this.show_library = false;
      this.upload_errors = [];
      this.$emit('input', references.slice(0, this.max));
    },
  },
};
</script>

<style lang="scss" scoped>
.references-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.reference-thumb,
.reference-drop {
  position: relative;
  width: 110px;
  height: 110px;
  border-radius: 0.5rem;
  overflow: hidden;
}
.reference-thumb {
  background-color: #f3f2f7;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .reference-kind {
    position: absolute;
    left: 0.35rem;
    bottom: 0.35rem;
    padding: 0.1rem 0.4rem;
    border-radius: 0.4rem;
    background-color: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 0.7rem;
  }
  .reference-remove {
    position: absolute;
    top: 0.35rem;
    right: 0.35rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.9);
    color: #5e5873;
    cursor: pointer;

    &:hover {
      color: #ea5455;
    }
  }
  &.reference-uploading {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7367f0;
  }
}
.reference-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem;
  border: 1px dashed #b9b9c3;
  color: #6e6b7b;
  text-align: center;
  font-size: 0.8rem;
  cursor: pointer;
  transition: border-color 200ms ease, background-color 200ms ease;

  small {
    color: #b9b9c3;
    font-size: 0.7rem;
  }
  &:hover,
  &.dragging {
    border-color: #7367f0;
    background-color: rgba(115, 103, 240, 0.06);
    color: #7367f0;
  }
}
.references-actions {
  gap: 0.5rem;

  .reference-kind-select {
    width: 160px;
  }
}
</style>
